import { useEffect, useMemo, useState } from "react";
import api, { formatError } from "../lib/api";
import { Button } from "../components/ui/button";
import { PageHeader } from "../components/GradientCard";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { toast } from "sonner";
import { Check, Edit, ChevronDown, ChevronUp, Award, X, BarChart3, TrendingUp, Clock, FileCheck } from "lucide-react";
import { GradientCard } from "../components/GradientCard";

const fmtINR = (n) => "₹ " + (Number(n) || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 });

export default function SalesAdmin({ embedded = false } = {}) {
  const [data, setData] = useState(null);
  const [pending, setPending] = useState([]);
  const [expanded, setExpanded] = useState({}); // user_id -> bool
  const [fy, setFy] = useState(null);
  const [tab, setTab] = useState("byuser");
  // Approval dialog state
  const [approveOrder, setApproveOrder] = useState(null);
  // Edit-salesmen dialog state
  const [editingOrder, setEditingOrder] = useState(null); // {order, salesmen[], edit_remark}
  // Monthly override dialog state
  const [overrideTarget, setOverrideTarget] = useState(null); // {user_id, user_name, year_month, current_value}

  const load = async () => {
    try {
      const url = fy ? `/sales-admin/by-user?fy=${fy}` : "/sales-admin/by-user";
      const [{ data: byUser }, { data: pendingOrders }] = await Promise.all([
        api.get(url), api.get("/sales-admin/pending"),
      ]);
      setData(byUser); setPending(pendingOrders);
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [fy]);

  const fyStartYear = data ? new Date(data.fy_start).getUTCFullYear() : new Date().getUTCFullYear();
  const fyOptions = []; for (let y = fyStartYear + 1; y >= fyStartYear - 3; y--) fyOptions.push(y);

  const grandTotals = useMemo(() => {
    if (!data?.rows) return { approved: 0, pending: 0 };
    return data.rows.reduce((acc, r) => ({ approved: acc.approved + r.fy_total_approved, pending: acc.pending + r.fy_total_pending }), { approved: 0, pending: 0 });
  }, [data]);

  const approveOne = async (orderId, userId) => {
    try {
      await api.post(`/orders/${orderId}/salesmen/approve`, { user_ids: [userId] });
      toast.success("Approved");
      load();
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };
  const approveAllOnOrder = async (orderId) => {
    try {
      await api.post(`/orders/${orderId}/salesmen/approve`, {});
      toast.success("All salesmen approved");
      load(); setApproveOrder(null);
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };

  return (
    <div className={embedded ? "space-y-6" : "max-w-[1500px] space-y-6"}>
      {!embedded && (
        <PageHeader
          moduleKey="sales_orders"
          tone="emerald"
          icon={BarChart3}
          eyebrow="Master Admin"
          title="Sales Administration"
          subtitle="Approve sales attribution, edit per-salesman shares, override monthly aggregates."
          actions={
            <select value={fy ?? fyStartYear} onChange={(e) => setFy(parseInt(e.target.value, 10))} className="h-10 px-3 border border-slate-200 rounded-md text-sm" data-testid="sa-fy">
              {fyOptions.map((y) => <option key={y} value={y}>FY {String(y).slice(-2)}-{String(y + 1).slice(-2)}</option>)}
            </select>
          }
        />
      )}
      {embedded && (
        <div className="flex justify-end">
          <select value={fy ?? fyStartYear} onChange={(e) => setFy(parseInt(e.target.value, 10))} className="h-9 px-3 border border-slate-200 rounded-md text-sm" data-testid="sa-fy">
            {fyOptions.map((y) => <option key={y} value={y}>FY {String(y).slice(-2)}-{String(y + 1).slice(-2)}</option>)}
          </select>
        </div>
      )}

      {data && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <GradientCard tone="emerald" icon={TrendingUp} title="Total Approved" subtitle={data.fy_label} testid="sa-total-approved">
            <div className="text-[28px] font-extrabold text-slate-900 mono leading-none">{fmtINR(grandTotals.approved)}</div>
          </GradientCard>
          <GradientCard tone="amber" icon={Clock} title="Total Pending" subtitle="Awaiting attribution" testid="sa-total-pending">
            <div className="text-[28px] font-extrabold text-slate-900 mono leading-none">{fmtINR(grandTotals.pending)}</div>
          </GradientCard>
          <GradientCard tone="indigo" icon={FileCheck} title="Orders Awaiting Approval" subtitle="Open queue" testid="sa-pending-orders-count">
            <div className="text-[28px] font-extrabold text-slate-900 mono leading-none">{pending.length}</div>
          </GradientCard>
        </div>
      )}

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="byuser" data-testid="sa-tab-byuser">By Salesman</TabsTrigger>
          <TabsTrigger value="approvals" data-testid="sa-tab-approvals">Pending Approvals ({pending.length})</TabsTrigger>
        </TabsList>

        <TabsContent value="byuser" className="mt-3">
          {!data || data.rows.length === 0
            ? <div className="pk-card p-12 text-center text-slate-400 text-sm">No sales attribution yet for {data?.fy_label || "this FY"}.</div>
            : (
              <div className="pk-card overflow-x-auto">
                <table className="w-full text-sm responsive-table">
                  <thead className="border-b border-slate-200 bg-slate-50/60">
                    <tr>
                      <th className="px-4 py-3 text-left pk-table-header w-8"></th>
                      <th className="px-4 py-3 text-left pk-table-header">Salesman</th>
                      <th className="px-4 py-3 text-right pk-table-header">Approved (FY)</th>
                      <th className="px-4 py-3 text-right pk-table-header">Pending (FY)</th>
                      <th className="px-4 py-3 text-left pk-table-header">Months</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.rows.map((r) => {
                      const isOpen = !!expanded[r.user_id];
                      const monthsList = Object.entries(r.monthly || {}).sort(([a],[b]) => a.localeCompare(b));
                      return (
                        <>
                          <tr key={r.user_id} className="border-b border-slate-100 hover:bg-slate-50/50" data-testid={`sa-row-${r.user_id}`}>
                            <td className="px-2 py-3" data-label="">
                              <button onClick={() => setExpanded({ ...expanded, [r.user_id]: !isOpen })} className="text-slate-500 hover:text-slate-800" data-testid={`sa-expand-${r.user_id}`}>
                                {isOpen ? <ChevronUp size={14}/> : <ChevronDown size={14}/>}
                              </button>
                            </td>
                            <td className="px-4 py-3 font-semibold text-slate-900" data-label="Salesman">{r.user_name}</td>
                            <td className="px-4 py-3 mono text-right text-emerald-700" data-label="Approved (FY)">{fmtINR(r.fy_total_approved)}</td>
                            <td className="px-4 py-3 mono text-right text-amber-700" data-label="Pending (FY)">{fmtINR(r.fy_total_pending)}</td>
                            <td className="px-4 py-3" data-label="Months">
                              <div className="flex gap-1 flex-wrap">
                                {monthsList.map(([k, v]) => (
                                  <button key={k} onClick={() => setOverrideTarget({ user_id: r.user_id, user_name: r.user_name, year_month: k, current_value: v.approved })}
                                    className={`text-[10px] mono px-2 py-1 rounded ${v.override ? "bg-blue-100 text-blue-700" : "bg-slate-100 text-slate-700"} hover:bg-slate-200`}
                                    data-testid={`sa-month-${r.user_id}-${k}`}>
                                    {k.slice(5)} ₹{Math.round(v.approved / 1000)}k{v.override ? " ✎" : ""}
                                  </button>
                                ))}
                              </div>
                            </td>
                          </tr>
                          {isOpen && (
                            <tr className="bg-slate-50/30" data-testid={`sa-orders-${r.user_id}`}>
                              <td colSpan={5} className="px-6 py-4">
                                <div className="text-[11px] uppercase tracking-wider font-semibold text-slate-600 mb-2">{r.orders.length} orders tagged</div>
                                <table className="w-full text-[12px]">
                                  <thead><tr className="text-slate-500 text-[10px] uppercase">
                                    <td className="py-1">Order</td><td>Client</td><td>%</td><td className="text-right">Amount</td><td>Status</td><td className="text-right">Actions</td>
                                  </tr></thead>
                                  <tbody>
                                    {r.orders.map((o) => (
                                      <tr key={o.order_id} className="border-t border-slate-200" data-testid={`sa-order-${r.user_id}-${o.order_no}`}>
                                        <td className="py-2 mono font-semibold">{o.order_no}</td>
                                        <td>{o.client_name}</td>
                                        <td className="mono">{o.share_percent}%</td>
                                        <td className="mono text-right">{fmtINR(o.share_amount)}</td>
                                        <td>{o.approval_status === "approved"
                                          ? <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100 text-[9px]">Approved</Badge>
                                          : <Badge className="bg-amber-100 text-amber-700 hover:bg-amber-100 text-[9px]">Pending</Badge>}</td>
                                        <td className="text-right">
                                          {o.approval_status !== "approved" && (
                                            <Button size="sm" variant="outline" onClick={() => approveOne(o.order_id, r.user_id)} className="h-7 text-[10px]" data-testid={`sa-approve-${o.order_no}-${r.user_id}`}>
                                              <Check size={10} className="mr-1"/> Approve
                                            </Button>
                                          )}
                                        </td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </td>
                            </tr>
                          )}
                        </>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )
          }
        </TabsContent>

        <TabsContent value="approvals" className="mt-3">
          {pending.length === 0
            ? <div className="pk-card p-12 text-center text-slate-400 text-sm">No pending approvals. All caught up.</div>
            : (
              <div className="space-y-3">
                {pending.map((o) => (
                  <div key={o.id} className="pk-card p-4" data-testid={`sa-pending-${o.order_no}`}>
                    <div className="flex items-start justify-between gap-4 flex-wrap">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="mono font-semibold text-slate-900">{o.order_no}</span>
                          <span className="text-sm text-slate-700">{o.client_name}</span>
                          <Badge className="bg-slate-100 text-slate-700 hover:bg-slate-100 text-[10px]">₹ {o.total_value}</Badge>
                          {o.is_agent_based && <Badge className="bg-purple-100 text-purple-700 hover:bg-purple-100 text-[10px]"><Award size={10} className="mr-0.5"/> Agent: {o.agent_name}</Badge>}
                        </div>
                        <div className="mt-2 flex flex-wrap gap-1.5">
                          {(o.salesmen || []).map((s) => (
                            <div key={s.user_id} className={`text-[11px] px-2 py-1 rounded-md border ${s.approval_status === "approved" ? "border-emerald-200 bg-emerald-50 text-emerald-800" : "border-amber-200 bg-amber-50 text-amber-800"}`}>
                              <span className="font-semibold">{s.user_name}</span> · {s.percent}% · {fmtINR(s.amount)}
                              {s.approval_status === "approved"
                                ? <Check size={10} className="inline ml-1"/>
                                : <button onClick={() => approveOne(o.id, s.user_id)} className="ml-1.5 text-amber-700 underline" data-testid={`sa-approve-inline-${o.order_no}-${s.user_id}`}>Approve</button>}
                            </div>
                          ))}
                        </div>
                      </div>
                      <div className="flex flex-col gap-2">
                        <Button size="sm" onClick={() => setApproveOrder(o)} className="h-8 bg-emerald-600 hover:bg-emerald-700 text-white text-xs" data-testid={`sa-approveall-${o.order_no}`}>
                          <Check size={11} className="mr-1"/> Approve All
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => setEditingOrder({ order: o, salesmen: [...(o.salesmen || [])].map((s) => ({ ...s })), edit_remark: "" })} className="h-8 text-xs" data-testid={`sa-edit-${o.order_no}`}>
                          <Edit size={11} className="mr-1"/> Edit Shares
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )
          }
        </TabsContent>
      </Tabs>

      {/* Approve-all confirm */}
      <Dialog open={!!approveOrder} onOpenChange={(o) => !o && setApproveOrder(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle className="heading">Approve all salesmen?</DialogTitle></DialogHeader>
          <div className="text-sm text-slate-700">
            Order <span className="font-semibold mono">{approveOrder?.order_no}</span> — {approveOrder?.client_name}<br/>
            <span className="text-slate-500 text-[12px]">All pending salesman entries will be marked Approved and credited to their Sales view.</span>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setApproveOrder(null)}>Cancel</Button>
            <Button onClick={() => approveAllOnOrder(approveOrder.id)} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="sa-approveall-confirm">Approve All</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit-salesmen dialog */}
      <EditSalesmenDialog editing={editingOrder} onClose={() => setEditingOrder(null)} onSaved={() => { setEditingOrder(null); load(); }} />

      {/* Monthly aggregate override dialog */}
      <OverrideDialog target={overrideTarget} onClose={() => setOverrideTarget(null)} onSaved={() => { setOverrideTarget(null); load(); }} />
    </div>
  );
}

function EditSalesmenDialog({ editing, onClose, onSaved }) {
  const [state, setState] = useState(editing);
  useEffect(() => { setState(editing); }, [editing]);
  if (!state) return null;

  const setRow = (idx, key, val) => {
    const arr = [...state.salesmen];
    arr[idx] = { ...arr[idx], [key]: val };
    setState({ ...state, salesmen: arr });
  };
  const removeRow = (idx) => setState({ ...state, salesmen: state.salesmen.filter((_, i) => i !== idx) });

  const save = async () => {
    if (!state.edit_remark.trim()) { toast.error("Edit remark is required"); return; }
    try {
      const payload = {
        edit_remark: state.edit_remark.trim(),
        salesmen: state.salesmen.map((s) => ({
          user_id: s.user_id,
          percent: parseFloat(s.percent) || 0,
          amount: parseFloat(s.amount) || undefined,
        })),
      };
      await api.patch(`/orders/${state.order.id}`, payload);
      toast.success("Sales attribution updated");
      onSaved();
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };

  return (
    <Dialog open={!!editing} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-xl" data-testid="sa-edit-dialog">
        <DialogHeader>
          <DialogTitle className="heading">Edit sales attribution — {state.order.order_no}</DialogTitle>
          <div className="text-[11px] text-slate-500">Adjust each salesman&apos;s % or override the exact ₹ amount. An edit remark is required.</div>
        </DialogHeader>
        <div className="space-y-2 py-2">
          {state.salesmen.length === 0 && <div className="text-xs italic text-slate-400">No salesmen on this order.</div>}
          {state.salesmen.map((s, i) => (
            <div key={s.user_id} className="flex items-center gap-2" data-testid={`sa-edit-row-${s.user_id}`}>
              <span className="text-sm flex-1 truncate">{s.user_name}</span>
              <Input type="number" step="0.01" value={s.percent} onChange={(e) => setRow(i, "percent", e.target.value)} className="h-8 w-20 text-xs" data-testid={`sa-edit-pct-${s.user_id}`} />
              <span className="text-[10px] text-slate-500">%</span>
              <Input type="number" step="0.01" value={s.amount} onChange={(e) => setRow(i, "amount", e.target.value)} className="h-8 w-28 text-xs" placeholder="Amount" data-testid={`sa-edit-amt-${s.user_id}`} />
              <Button size="sm" variant="outline" onClick={() => removeRow(i)} className="h-8 w-8 p-0 text-red-600"><X size={11}/></Button>
            </div>
          ))}
          <div className="pt-3 border-t">
            <Label className="text-[11px] uppercase tracking-wider font-semibold text-amber-700">Edit Remark *</Label>
            <Textarea rows={2} value={state.edit_remark} onChange={(e) => setState({ ...state, edit_remark: e.target.value })} placeholder="Why are you adjusting the shares?" className="mt-1" data-testid="sa-edit-remark" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={save} className="bg-[var(--pk-accent)] hover:bg-[var(--pk-accent-hover)] text-white" data-testid="sa-edit-save">Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function OverrideDialog({ target, onClose, onSaved }) {
  const [val, setVal] = useState("");
  const [note, setNote] = useState("");
  useEffect(() => {
    if (target) { setVal(String(target.current_value || 0)); setNote(""); }
  }, [target]);
  if (!target) return null;

  const save = async () => {
    try {
      await api.post("/sales-admin/aggregate-override", {
        user_id: target.user_id, year_month: target.year_month,
        override_amount: parseFloat(val) || 0, note,
      });
      toast.success("Aggregate override saved");
      onSaved();
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };
  const remove = async () => {
    try {
      await api.delete(`/sales-admin/aggregate-override?user_id=${target.user_id}&year_month=${target.year_month}`);
      toast.success("Override removed");
      onSaved();
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };
  return (
    <Dialog open={!!target} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="sa-override-dialog">
        <DialogHeader>
          <DialogTitle className="heading">Monthly override — {target.user_name}</DialogTitle>
          <div className="text-[11px] text-slate-500">Override the auto-computed approved total for <span className="font-semibold mono">{target.year_month}</span>. Useful for manual corrections or carry-overs.</div>
        </DialogHeader>
        <div className="space-y-3 py-2">
          <div>
            <Label className="text-[11px] uppercase tracking-wider font-semibold text-slate-600">Override Amount (₹)</Label>
            <Input type="number" step="0.01" value={val} onChange={(e) => setVal(e.target.value)} className="mt-1" data-testid="sa-override-amount" />
          </div>
          <div>
            <Label className="text-[11px] uppercase tracking-wider font-semibold text-slate-600">Note (optional)</Label>
            <Textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)} className="mt-1" placeholder="Why is this overridden?" data-testid="sa-override-note" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={remove} className="text-red-600 border-red-200" data-testid="sa-override-remove">Remove Override</Button>
          <Button onClick={save} className="bg-[var(--pk-accent)] hover:bg-[var(--pk-accent-hover)] text-white" data-testid="sa-override-save">Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
